/* eslint-disable no-prototype-builtins */
import { activeCompany, sessionCompanyId, user } from './userSelectors';
import { sortByProp } from '@shared/utils/sortByProp.js';
import { flattenGraphSequence } from '@shared/graph/flattenGraphSequence.js';
import { createSelector } from '@/redux/lib/createSelector.js';
import { filter } from './uiSelectors';
import workflowStageOptions from '@/schema/workflowStageOptions';
import { query } from './querySelectors';
import selectors from '@/redux/selectors/selectors';
import { compareDesc } from 'date-fns';

const stableArray = [];

const param = createSelector(
  'param',
  [(state) => state.ui.params, (_, key) => key], //
  (params, key) => params?.[key]
);

export const workflowId = createSelector(
  'workflowId',
  [
    (
      state //
    ) => selectors.param(state, 'workflowId'),
    (state) => selectors.query(state, 'workflowId')
  ],
  (workflowIdParam, workflowIdQuery) => workflowIdParam || workflowIdQuery
);

export const workflowVersion = createSelector(
  'workflowVersion',
  [
    (
      state //
    ) => selectors.param(state, 'workflowVersion'),
    (state) => selectors.query(state, 'workflowVersion')
  ],
  (workflowVersionParam, workflowVersionQuery) => workflowVersionParam || workflowVersionQuery
);

export const workflowsMetadata = (state) => state.workflow.workflowsMetadata;
export const cirraWorkflows = (state) => state.workflow.cirraWorkflows;
export const cirraOrganizationWorkflows = createSelector(
  'cirraOrganizationWorkflows',
  [cirraWorkflows], //
  (workflows) => (Array.isArray(workflows) ? workflows.filter((workflow) => workflow?.isCompany === true) : stableArray)
);
export const workflowMetadataById = createSelector('workflowMetadataById', [workflowsMetadata, (_, workflowId) => workflowId], (workflowsMetadata, workflowId) => {
  return workflowsMetadata?.find((n) => n?.id === workflowId);
});
export const workflowList = createSelector('workflowList', [workflowsMetadata, activeCompany, (state) => query(state, 'tab')], (workflowsMetadata, company, tab) => {
  const activeTab = tab || 'workflow';
  const workflowStagesIsEnabled = company?.settings?.workflowStages === true;

  if (workflowStagesIsEnabled && activeTab === 'workflow') {
    const groupsByStage = (workflowsMetadata || []).reduce((acc, workflow) => {
      const normalizedStage = workflow?.stage || 'requirements';
      if (!acc[normalizedStage]) {
        acc[normalizedStage] = [workflow];
      } else {
        acc[normalizedStage].push(workflow);
      }
      return acc;
    }, {});

    const grouped = [];
    for (const option of workflowStageOptions) {
      const stage = option.value;
      const group = groupsByStage[stage];
      if (group && group.length > 0) {
        grouped.push(group);
      }
    }
    return grouped;
  }

  return workflowsMetadata;
});
export const rawWorkflows = (state) => state?.workflow?.workflows;

export const workflows = createSelector('workflows', rawWorkflows, sessionCompanyId, workflowsMetadata, user, (workflows, companyId, workflowsMetadata, user) =>
  workflows
    ?.filter((n) => n?.companyId === companyId || user?.jwt?.role === 'Admin')
    .map((n) => {
      const fullSequence = flattenGraphSequence(n.graph, n.steps, undefined, true).filter((n) => n.type !== 'folder');
      const meta = workflowsMetadata?.find((m) => m?.id === n?.id) || {};
      const workflow = { ...n, steps: n?.steps?.map((s) => ({ ...s, stepNumber: fullSequence.findIndex((m) => m.id === s.id) + 1 })), meta };
      if (meta) {
        workflow.meta = meta;
      }
      return workflow;
    })
);
export const activeWorkflow = createSelector(
  'activeWorkflow',
  [(state) => param(state, 'workflowId'), (state) => param(state, 'workflowVersion'), workflows, workflowsMetadata], //
  (workflowId, workflowVersion, workflows, workflowsMetadata) => {
    const meta = workflowsMetadata?.find((m) => m?.id === workflowId);
    const workflow = workflows?.find((w) => w.id === workflowId && w.version === workflowVersion);
    if (workflow && meta) {
      workflow.meta = meta;
    }
    return workflow;
  }
);
export const activeWorkflowMemories = createSelector('activeWorkflowMemories', activeWorkflow, (workflow) => {
  const result = [];
  const workflowId = workflow?.id;
  const memory = workflow?.variables?.memory;

  const addToResult = (memoryKey, memoryValue, opts = {}) => {
    result.push({
      memoryKey,
      memoryValue,
      workflowId,
      ...opts
    });
  };

  // Process global variables
  if (memory?.global) {
    for (const key in memory?.global) {
      if (memory?.global.hasOwnProperty(key)) {
        addToResult(key, memory?.global[key], { isGlobal: true, scope: 'allWorkflows' });
      }
    }
  }

  // Process environment memory
  if (memory?.environment) {
    for (const key in memory?.environment) {
      if (memory?.environment.hasOwnProperty(key)) {
        addToResult(key, memory?.environment[key], { isEnvironment: true, scope: 'environment' });
      }
    }
  }

  // Process installation memory
  if (memory?.installation) {
    for (const key in memory?.installation) {
      if (memory?.installation.hasOwnProperty(key)) {
        addToResult(key, memory?.installation[key], { isInstallation: true, scope: 'installation' });
      }
    }
  }

  // Process non-global variables
  for (const key in memory) {
    if (key !== 'global' && key !== 'environment' && key !== 'installation' && memory.hasOwnProperty(key)) {
      addToResult(key, memory[key], { scope: 'workflow' });
    }
  }

  return result;
});

export const workflowsByCompany = createSelector(
  'workflowsByCompany',
  rawWorkflows,
  (_, companyId) => companyId,
  (workflows, companyId) => workflows?.filter((n) => n?.companyId === companyId)
);

export const credentials = createSelector(
  'credentials',
  (state) => state.workflow.credentials,
  (credentials) => [...credentials].sort(sortByProp('id'))
);
export const companyCredentials = createSelector('companyCredentials', activeCompany, credentials, (company, credentials) => credentials?.filter((n) => n?.companyId === company?.id));
export const credentialsByCompany = createSelector(
  'credentialsByCompany',
  (state) => state.workflow.credentialsByCompany,
  (_, companyId) => companyId,
  (credentials, companyId) => {
    return credentials?.[companyId] || [];
  }
);
export const credentialById = createSelector(
  'credentialById',
  credentials,
  (_, id) => id,
  (creds, id) => creds.find((n) => n.credentialId === id)
);

export const credentialsByAppId = createSelector(
  'credentialsByAppId',
  credentials,
  (_, appId) => appId,
  (creds, appId) => creds.filter((n) => n.appId === appId)
);
export const workflowById = createSelector(
  'workflowById',
  workflows,
  (_, workflowId, __) => workflowId,
  (_, __, workflowVersion) => workflowVersion,
  (workflow, workflowId, workflowVersion = 'draft') => workflow.find((n) => n.id === workflowId && n.version === workflowVersion)
);

export const workflowLatestById = createSelector('workflowLatestById', [workflows, (_, workflowId) => workflowId], (workflow, workflowId) => {
  const nonDraft = workflow.find((n) => n.id === workflowId && n.version !== 'draft');
  return nonDraft || workflow.find((n) => n.id === workflowId && n.version === 'draft');
});
export const workflowLastUpdateById = createSelector('workflowLastUpdateById', workflowById, (workflow) => {
  if (!workflow) {
    return;
  }
  const stepsUpdates = workflow?.steps?.map((step) => step?.updatedOn) || stableArray;
  const allUpdates = [workflow?.updatedOn, ...stepsUpdates];
  const mappedUpdates = allUpdates.map((date) => new Date(date).getTime()).filter((n) => !isNaN(n));
  if (mappedUpdates.length === 0) {
    return;
  }
  return new Date(Math.max(...mappedUpdates));
});

export const workflowIsDraft = createSelector('workflowIsDraft', workflowById, (workflow) => {
  if (!workflow) {
    return false;
  }

  const stepsUpdates = workflow?.steps?.map((step) => step?.updatedOn) || stableArray;
  const allUpdates = [workflow?.updatedOn, ...stepsUpdates];
  const mappedUpdates = allUpdates.map((date) => new Date(date).getTime()).filter((n) => !isNaN(n));

  const workflowLastUpdate = mappedUpdates.length > 0 ? new Date(Math.max(...mappedUpdates)) : undefined;
  const workflowPublishedOn = workflow?.publishedOn;

  if (!workflowPublishedOn || !workflowLastUpdate) {
    return true;
  }

  return compareDesc(new Date(workflowPublishedOn), workflowLastUpdate) === 1;
});

export const workflowLogs = (state) => state.workflow?.workflowLogs;
export const workflowRunDetailsLogs = (state) => state.workflow?.workflowRunDetailsLogs;
export const workflowLogsById = createSelector(
  'workflowLogsById',
  workflowLogs,
  (_, logId) => logId,
  (n, logId) => n.find((n) => n.id === logId)
);
export const workflowLogOperationLogs = (state) => state.workflow?.workflowLogOperationLogs;
export const workflowLogOperationLogsById = createSelector(
  'workflowLogOperationLogsById',
  workflowLogOperationLogs,
  (_, logId) => logId,
  (n, logId) => n[logId]
);
export const workflowLogOutput = (state) => state.workflow?.workflowLogOutput;
export const workflowLogOutputById = createSelector(
  'workflowLogOutputById',
  workflowLogOutput,
  (_, logId) => logId,
  (n, logId) => n[logId]
);

export const workflowOperationLogsByLogId = createSelector(
  //
  'workflowOperationLogsByLogId',
  workflowLogOperationLogsById,
  workflowLogOutputById,
  (operationLogs, logOutput) => {
    if (logOutput?.operationLogs) {
      return logOutput.operationLogs;
    }
    return operationLogs || [];
  }
);

export const workflowLogsMetadata = (state) => state.workflow?.workflowLogsMetadata;
export const workflowLogsMetadataSnapshots = createSelector(
  'workflowLogsMetadataSnapshots',
  [workflowLogsMetadata], //
  (workflowLogsMetadata) => workflowLogsMetadata?.filter((n) => n?.type === 'snapshot')
);
export const workflowLogsMetadataEnvironments = createSelector(
  'workflowLogsMetadataEnvironments',
  [workflowLogsMetadata], //
  (workflowLogsMetadata) => workflowLogsMetadata?.filter((n) => n?.type === 'environment')
);
export const workflowLogsMetadataStatuses = createSelector(
  'workflowLogsMetadataStatuses',
  [workflowLogsMetadata], //
  (workflowLogsMetadata) => workflowLogsMetadata?.filter((n) => n?.type === 'status')
);
export const workflowLogsMetadataOperations = createSelector(
  'workflowLogsMetadataOperations',
  [workflowLogsMetadata], //
  (workflowLogsMetadata) => workflowLogsMetadata?.filter((n) => n?.type === 'operations')
);
export const workflowLogsMetadataEndUsers = createSelector(
  'workflowLogsMetadataEndUsers',
  [workflowLogsMetadata], //
  (workflowLogsMetadata) => workflowLogsMetadata?.filter((n) => n?.type === 'endUser')
);
export const workflowLogsMetadataInstallations = createSelector(
  'workflowLogsMetadataInstallations',
  [workflowLogsMetadata], //
  (workflowLogsMetadata) => workflowLogsMetadata?.filter((n) => n?.type === 'installation')
);
export const workflowLogOperationLogSuccesses = createSelector(
  'workflowLogOperationLogSuccesses',
  [(state, logId) => workflowLogOperationLogsById(state, logId)], //
  (n) => n?.filter((log) => log.actionStatus === 'success')
);
export const workflowLogOperationLogErrors = createSelector(
  'workflowLogOperationLogErrors',
  [(state, logId) => workflowLogOperationLogsById(state, logId)], //
  (n) => n?.filter((log) => log.actionStatus === 'error')
);
export const workflowLogOperationLogWarnings = createSelector(
  'workflowLogOperationLogWarnings',
  [(state, logId) => workflowLogOperationLogsById(state, logId)], //
  (n) => n?.filter((log) => log.actionStatus === 'warn')
);
export const workflowLogOperationLogInfos = createSelector(
  'workflowLogOperationLogInfos',
  [(state, logId) => workflowLogOperationLogsById(state, logId)], //
  (n) => n?.filter((log) => log.actionStatus === 'info')
);
export const workflowLogStepOutputByStepId = createSelector(
  'workflowLogStepOutputByStepId',
  [(_, __, stepId) => stepId, (state, logId) => workflowLogOutputById(state, logId)], //
  (stepId, n) => n?.meta?.steps?.find((log) => log?.id === stepId)
);

export const updateState = (state) => state.workflow?.updateState;
export const workflowStepOutputUpdate = createSelector(
  'workflowStepOutputUpdate',
  [(state) => param(state, 'workflowId'), updateState], //
  (workflowId, update) => update?.stepOutputUpdate?.[workflowId]
);
export const workflowWebhookResponseUpdate = createSelector(
  'workflowWebhookResponseUpdate',
  [(state) => param(state, 'workflowId'), updateState], //
  (workflowId, update) => update?.webhookResponseUpdate?.[workflowId]
);

export const appsVersions = (state) => state.workflow?.appsVersions;

export const workflowStepOutputStepId = createSelector(
  'workflowStepOutputStepId',
  activeWorkflow,
  appsVersions,
  (_, stepId) => stepId,
  (workflow, apps, stepId) => {
    const activeStep = workflow?.steps?.find((n) => n.id === stepId);
    const activeApp = apps?.find((n) => n.id === activeStep?.appId);
    return activeApp?.slug === 'iterate' ? activeStep?.arguments?.stepId : stepId;
  }
);
export const workflowStepOutputs = (state) => state.workflow?.stepOutput;
export const workflowStepOutput = createSelector('workflowStepOutput', workflowStepOutputStepId, workflowStepOutputs, (stepId, outputs) => {
  return outputs?.[stepId];
});

export const appsMetadata = createSelector(
  'appsMetadata',
  (state) => state.workflow?.appsMetadata,
  (appsMetadata) => (appsMetadata ? [...appsMetadata].sort(sortByProp(['workflowCount', 'name'], ['desc', 'asc'])) : [])
);

export const actionUsage = createSelector(
  'actionUsage',
  [appsVersions, (_, appId, __) => appId, (_, __, actionId) => actionId], //
  (apps, appId, actionId) => {
    const app = apps.find((n) => n.id === appId) || {};
    return app.workflowActionUsages?.[actionId] ?? 0;
  }
);
export const appById = createSelector(
  'appById',
  [(state) => state.workflow.appsVersions, (_, id) => id, (_, __, version) => version], //
  (apps, id, version) => apps?.find((n) => (version ? n.id === id && n.version === version : n.id === id))
);
export const appsMetadataBySlug = createSelector(
  'appsMetadataBySlug',
  appsMetadata,
  (_, appSlug) => appSlug,
  (appsMetadata, appSlug) => appsMetadata?.find((n) => n.slug === appSlug)
);
export const appsMetadataById = createSelector(
  'appsMetadataById',
  appsMetadata,
  (_, id) => id,
  (appsMetadata, id) => appsMetadata?.find((n) => n.id === id)
);
export const appActionOutputs = (state) => state.workflow?.appActionOutputs;
export const appActionOutboundOutputs = (state) => state.workflow?.appActionOutboundOutputs;

export const cookedWorkflowStepOutputs = createSelector('cookedWorkflowStepOutputs', activeWorkflow, appsVersions, workflowStepOutputs, (workflow, apps, outputs) => {
  return Object.fromEntries(
    Object.entries(outputs).map(([stepId, output]) => {
      const activeStep = workflow?.steps?.find((n) => n.id === stepId);
      const activeApp = apps?.find((n) => n.id === activeStep?.appId);
      const isIterate = activeApp?.slug === 'iterate';
      const newOutput = isIterate
        ? outputs[activeStep?.arguments?.stepId] || output //
        : output;
      return [stepId, newOutput];
    })
  );
});

export const lastStepId = createSelector(
  'lastStepId',
  activeWorkflow,
  (_, parentStepId) => parentStepId,
  (workflow, parentStepId) => {
    const steps = workflow?.steps || [];
    const graph = workflow?.graph || {};
    const appSteps = flattenGraphSequence(graph, steps, parentStepId);
    return appSteps[appSteps.length - 1]?.id;
  }
);

export const workflowSnapshots = (state) => state.workflow?.workflowSnapshots;
export const workflowAutosaves = (state) => state.workflow?.workflowAutosaves;
export const lastAutosaveCreatedOn = (state) => state.workflow?.workflowAutosaves?.[0]?.createdOn;
export const userWorkflowAlerts = (state) => state.workflow?.userWorkflowAlerts;
export const allPublishedWorkflows = (state) => state.workflow?.allPublishedWorkflows;
export const actionSuggestions = (state) => state.workflow?.actionSuggestions || {};
export const activeActionParseImportResults = createSelector(
  'activeActionParseImportResults',
  [(state) => param(state, 'appId'), (state) => param(state, 'version'), (state) => param(state, 'arg1'), actionSuggestions], //
  (appId, version, actionId, suggestions) => suggestions?.[appId]?.[version]?.[actionId]
);

export const workflowTags = (state) => state.workflow?.workflowTags;
export const workflowTemplateTags = (state) => state.workflow?.workflowTemplateTags;
export const recipes = (state) => state.workflow?.recipes;
export const recipeApps = createSelector('recipeApps', [recipes, (state) => filter(state, 'recipeShowAll')], (recipes, showAllWorkflows) => {
  const filteredRecipes = recipes?.filter((recipe) => showAllWorkflows?.value || recipe.isLiveWorkflow !== true) || [];
  const apps = [...new Set(filteredRecipes?.flatMap((n) => n?.apps?.map((app) => (app?.category === 'app' ? app : null))))]?.filter((n) => !!n);

  const uniqueApps = apps.reduce((acc, app) => {
    if (!acc.find((a) => a.id === app.id)) {
      acc.push(app);
    }
    return acc;
  }, []);

  return uniqueApps?.sort((a, b) => a?.name?.localeCompare(b?.name));
});
export const recipeCategories = createSelector('recipeCategories', recipes, (recipes) => {
  return [...new Set(recipes?.map((n) => n?.category))]?.filter((n) => !!n)?.sort((a, b) => a?.localeCompare(b));
});
export const recipeTags = createSelector('recipeTags', recipes, workflowTemplateTags, workflowTags, (recipes, workflowTemplateTags, workflowTags) => {
  const tags = [...new Set(recipes?.flatMap((n) => n?.tags))];
  const catalog = new Map([...(workflowTemplateTags || []), ...(workflowTags || [])].map((tag) => [tag?.id, tag]));

  return [...catalog.values()]?.filter((n) => tags?.includes(n?.id))?.sort((a, b) => a?.label?.localeCompare(b?.label));
});

export const tutorialScripts = (state) => state?.workflow?.tutorialScripts;

export const activeWorkflowIntegrationId = createSelector('activeWorkflowIntegrationId', activeWorkflow, (workflow) => {
  return workflow?.integrationId;
});
